// ✅ Trekkin.app — main.jsx v3.4.1 FINAL (LOGGER SAFE + iOS OK)
// —————————————————————————————————————————————
// Inclui:
//   ✔ App.jsx
//   ✔ global.css
//   ✔ Native GPS Bridge
//   ✔ Build info
//   ✔ Service Worker
//   ✔ Live Share Viewer
//   ✔ URL queries (?share=XYZ, ?mode=resetPassword)
//   ✔ ToastContainer (GLOBAL)
//   ✔ DeepLinkHandler (RESET PASSWORD)
//   ✔ Logger GLOBAL (iOS safe)
// —————————————————————————————————————————————

import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import App from "./App.jsx";
import "./styles/global.css";

import { logger } from "./utils/logger.js";
import { purgeExpiredMapCaches } from "./utils/mapCache.js";
import { startRtdbTrafficMonitor } from "./utils/rtdbTrafficMonitor.js";
import { logEvent, useLogsStore } from "./stores/useLogsStore.jsx";
import { initWaypointOpsAutoFlush } from "./utils/waypointOpsQueue.js";
import { initSegmentDefinitionOpsAutoFlush } from "./utils/segmentDefinitionOpsQueue.js";
import { initSegmentAttemptsAutoFlush } from "./utils/segments.js";
import { initSummitConquestOpsAutoFlush } from "./utils/summitConquestQueue.js";
import { getPendingOp } from "./utils/crashBreadcrumb.js";
import { initializeTheme } from "./stores/useThemeStore.jsx";

// -------------------------------------------------------------
//  TOAST SYSTEM
// -------------------------------------------------------------
import { ToastContainer } from "./components/NotifyToast.jsx";

// -------------------------------------------------------------
//  PANELS STORE (necessário para reset-password deep link)
// -------------------------------------------------------------
import { usePanelsStore } from "./stores/usePanelsStore.jsx";

// -------------------------------------------------------------
//  NATIVE GPS BRIDGE (iOS / Android → JS)
// -------------------------------------------------------------
import "./utils/native-gps-bridge.js";

const showFatalOverlay = (title, details = "") => {
  try {
    if (typeof document === "undefined") return;
    const id = "trekkin-fatal-overlay";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("div");
      el.id = id;
      el.style.position = "fixed";
      el.style.inset = "0";
      el.style.zIndex = "999999";
      el.style.background = "#0b1020";
      el.style.color = "#f8fafc";
      el.style.padding = "16px";
      el.style.fontFamily = "ui-monospace, SFMono-Regular, Menlo, monospace";
      el.style.fontSize = "12px";
      el.style.overflow = "auto";
      document.body.appendChild(el);
    }
    const safeTitle = String(title || "FATAL ERROR");
    const safeDetails = String(details || "").slice(0, 6000);
    el.innerHTML = `<div style="font-size:14px;font-weight:700;margin-bottom:8px;">${safeTitle}</div><pre style="white-space:pre-wrap;line-height:1.35;">${safeDetails}</pre>`;
  } catch {}
};

const isNativeWebViewRuntime = () => {
  try {
    if (typeof window === "undefined" || typeof navigator === "undefined") return false;
    if (window?.Capacitor?.isNativePlatform?.()) return true;
    const ua = String(navigator.userAgent || "");
    const nativeFileUrl = /^file:/i.test(String(window.location?.href || ""));
    return nativeFileUrl && /Android|iPhone|iPad|iPod/i.test(ua);
  } catch {
    return false;
  }
};

const isQuotaExceededError = (err) => {
  if (!err) return false;
  const name = String(err?.name || "");
  const message = String(err?.message || "");
  return (
    name === "QuotaExceededError" ||
    /quota/i.test(name) ||
    /quota/i.test(message) ||
    err?.code === 22 ||
    err?.code === 1014
  );
};

const purgeStorageForQuota = (failingKey = "") => {
  if (typeof localStorage === "undefined") return { removed: 0, bytes: 0 };
  const removed = [];
  let bytes = 0;

  const removeKey = (key) => {
    if (!key) return;
    try {
      const val = localStorage.getItem(key);
      if (val == null) return;
      bytes += String(val).length;
      localStorage.removeItem(key);
      removed.push(key);
    } catch {}
  };

  const priorityKeys = [
    "trekkin_waypoints_cache_v1",
    "trekkin_waypoints_cache_meta_v1",
    "trekkin_nav_grid_v1",
    "firebase:previous_websocket_failure",
    "trekkin_rtdb_monitor",
    "trekkin_rtdb_monitor_buffer",
    "trekkin.logs.buffer",
    "trekkin.logs.queue",
  ];
  priorityKeys.forEach(removeKey);
  // Nunca apaga histórico para conseguir reescrever histórico. Os callers
  // críticos precisam receber o erro e aplicar os seus fallbacks.
  if (failingKey !== "historygps") removeKey(failingKey);

  try {
    const dynamic = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (/^firebase:/i.test(k)) dynamic.push(k);
      else if (/waypoints.*cache|events.*cache|map.*cache/i.test(k)) dynamic.push(k);
    }
    dynamic.forEach(removeKey);
  } catch {}

  return { removed: removed.length, bytes, keys: removed.slice(0, 20) };
};

const installLocalStorageQuotaGuard = () => {
  if (typeof window === "undefined" || typeof Storage === "undefined") return;
  if (!isNativeWebViewRuntime()) return;
  if (window.__TREKKIN_STORAGE_QUOTA_GUARD__ === true) return;
  const proto = Storage?.prototype;
  const originalSetItem = proto?.setItem;
  if (typeof originalSetItem !== "function") return;

  const shouldLogQuota = (type, key, minMs = 60000) => {
    try {
      const now = Date.now();
      const state =
        window.__TREKKIN_STORAGE_QUOTA_LOG_TS__ ||
        (window.__TREKKIN_STORAGE_QUOTA_LOG_TS__ = {});
      const id = `${type}:${String(key || "")}`;
      const last = Number(state[id] || 0);
      if (last && now - last < minMs) return false;
      state[id] = now;
      return true;
    } catch {
      return true;
    }
  };

  proto.setItem = function patchedSetItem(key, value) {
    try {
      return originalSetItem.call(this, key, value);
    } catch (err) {
      if (!isQuotaExceededError(err)) throw err;
      const recovery = purgeStorageForQuota(String(key || ""));
      if (shouldLogQuota("recovery", key)) {
        try {
          logger.warn("STORAGE", "Quota recovery triggered", {
            key: String(key || ""),
            removed: recovery?.removed || 0,
            approxBytesFreed: recovery?.bytes || 0,
          });
          logEvent("warn", "STORAGE_QUOTA_RECOVERY", {
            key: String(key || ""),
            removed: recovery?.removed || 0,
            approxBytesFreed: recovery?.bytes || 0,
            keys: recovery?.keys || [],
          });
        } catch {}
      }
      try {
        return originalSetItem.call(this, key, value);
      } catch (retryErr) {
        if (!isQuotaExceededError(retryErr)) throw retryErr;
        if (shouldLogQuota("skipped", key)) {
          try {
            logger.error("STORAGE", "Quota write skipped", {
              key: String(key || ""),
              message: String(retryErr?.message || retryErr),
            });
            logEvent("error", "STORAGE_QUOTA_WRITE_SKIPPED", {
              key: String(key || ""),
              message: String(retryErr?.message || retryErr),
            });
          } catch {}
        }
        if (String(key || "") === "historygps") throw retryErr;
        // Evita crash global em WebView quando um setItem não-crítico falha por quota.
        return undefined;
      }
    }
  };

  window.__TREKKIN_STORAGE_QUOTA_GUARD__ = true;
};

// -------------------------------------------------------------
//  🔎 LOGGER GLOBAL (SAFE)
// -------------------------------------------------------------
const consoleSnapshot = (() => {
  try {
    return {
      log: console.log.bind(console),
      info: console.info.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      debug: console.debug ? console.debug.bind(console) : console.log.bind(console),
      trace: console.trace ? console.trace.bind(console) : console.log.bind(console),
    };
  } catch {
    return {
      log: () => {},
      info: () => {},
      warn: () => {},
      error: () => {},
      debug: () => {},
      trace: () => {},
    };
  }
})();

const setConsoleEnabled = (enabled) => {
  const fn = enabled ? consoleSnapshot : null;
  if (!fn) {
    console.log = () => {};
    console.info = () => {};
    console.warn = () => {};
    console.error = () => {};
    console.debug = () => {};
    console.trace = () => {};
    return;
  }
  console.log = fn.log;
  console.info = fn.info;
  console.warn = fn.warn;
  console.error = fn.error;
  console.debug = fn.debug;
  console.trace = fn.trace;
};

const isForcedConsoleDebug = () => {
  try {
    if (typeof window === "undefined") return false;
    if (window.__TREKKIN_DEBUG_CONSOLE__ === true) return true;
    if (localStorage.getItem("trekkin.debug.console") === "1") return true;
    if (localStorage.getItem("debugLogs") === "1") return true;
    const sp = new URLSearchParams(window.location.search || "");
    return sp.get("debugConsole") === "1";
  } catch {
    return false;
  }
};

const shouldKeepConsoleEnabled = () => !!(import.meta.env.DEV || isForcedConsoleDebug());

const syncConsoleState = (logsEnabled) => {
  const consoleEnabled = shouldKeepConsoleEnabled();
  setConsoleEnabled(consoleEnabled);
  // logger mantém capacidade de alimentar painel de logs mesmo com console OFF
  logger.setEnabled(consoleEnabled);
};

const hasStoredLogsPreference = (() => {
  try {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("trekkin.logs.enabled") !== null;
  } catch {
    return false;
  }
})();

if (import.meta.env.DEV && !hasStoredLogsPreference) {
  try {
    useLogsStore.getState()?.setLogsEnabled?.(true);
  } catch {}
}

const initialLogsEnabled = !!useLogsStore.getState()?.logsEnabled;
syncConsoleState(initialLogsEnabled);
useLogsStore.subscribe((state, prevState) => {
  if (state?.logsEnabled === prevState?.logsEnabled) return;
  syncConsoleState(!!state?.logsEnabled);
});
// ❗ persistência NÃO ativa por defeito (só via Admin / gpsDebug)

window.logger = logger;
installLocalStorageQuotaGuard();
initializeTheme();
initWaypointOpsAutoFlush();
initSegmentDefinitionOpsAutoFlush();
initSegmentAttemptsAutoFlush();
initSummitConquestOpsAutoFlush();

const installIosCrashLoopGuard = () => {
  try {
    if (typeof window === "undefined") return;
    const isNative = !!window?.Capacitor?.isNativePlatform?.();
    const platform = String(window?.Capacitor?.getPlatform?.() || "").toLowerCase();
    if (!isNative || platform !== "ios") return;

    const now = Date.now();
    const crashCountKey = "trekkin.ios.crashLoop.count";
    const bootStartKey = "trekkin.ios.crashLoop.bootStart";
    const guardKey = "trekkin.ios.safeMode";
    const guardUntilKey = "trekkin.ios.safeMode.until";
    const windowMs = 90 * 1000;
    const stableMs = 20 * 1000;
    const safeModeMs = 15 * 60 * 1000;

    const lastBoot = Number(localStorage.getItem(bootStartKey) || 0) || 0;
    let count = Number(localStorage.getItem(crashCountKey) || 0) || 0;
    if (lastBoot > 0 && now - lastBoot < windowMs) count += 1;
    else count = 1;

    localStorage.setItem(crashCountKey, String(count));
    localStorage.setItem(bootStartKey, String(now));

    const guardUntil = Number(localStorage.getItem(guardUntilKey) || 0) || 0;
    const guardActive = guardUntil > now || count >= 3;
    if (guardActive) {
      const until = Math.max(guardUntil, now + safeModeMs);
      localStorage.setItem(guardKey, "1");
      localStorage.setItem(guardUntilKey, String(until));
      window.__TREKKIN_IOS_SAFE_MODE__ = true;
      logger.warn("BOOT", "iOS crash-loop safe mode ativo", { count, until });
      logEvent("warn", "IOS_CRASH_LOOP_SAFE_MODE_ON", { count, until });
    } else {
      window.__TREKKIN_IOS_SAFE_MODE__ = false;
    }

    window.setTimeout(() => {
      try {
        localStorage.setItem(crashCountKey, "0");
        localStorage.removeItem(bootStartKey);
        const until = Number(localStorage.getItem(guardUntilKey) || 0) || 0;
        if (until > 0 && Date.now() >= until) {
          localStorage.removeItem(guardUntilKey);
          localStorage.removeItem(guardKey);
          window.__TREKKIN_IOS_SAFE_MODE__ = false;
          logger.info("BOOT", "iOS safe mode desativado após estabilidade");
          logEvent("info", "IOS_CRASH_LOOP_SAFE_MODE_OFF", {});
        }
      } catch {}
    }, stableMs);
  } catch {}
};
installIosCrashLoopGuard();

const shouldEnableRtdbMonitorOnBoot = (() => {
  try {
    if (import.meta.env.DEV) return true;
    if (typeof window === "undefined") return false;
    return (
      window.__TREKKIN_ENABLE_RTDB_MONITOR__ === true ||
      localStorage.getItem("trekkin_rtdb_monitor") === "1"
    );
  } catch {
    return false;
  }
})();

const bootPerfStart =
  typeof performance !== "undefined" ? performance.now() : 0;
window.__TREKKIN_BOOT_T0 = bootPerfStart || Date.now();
console.log(
  `[BOOT] perfStart=${bootPerfStart} ts=${Date.now()}`
);
console.log(
  `[BOOT] console=${shouldKeepConsoleEnabled() ? "forced/dev-on" : "off-by-default"} logsPanel=${
    initialLogsEnabled ? "on" : "off"
  }`
);

logger.info("BOOT", "Logger inicializado");
purgeExpiredMapCaches().catch((err) => {
  logger.warn("MAP_CACHE", "Falha ao limpar cache expirada", {
    message: err?.message || String(err || "unknown"),
  });
});
try {
  const pending = getPendingOp();
  if (pending?.opId) {
    logger.warn("CRASH_GUARD", "Operação pendente recuperada no arranque", pending);
    logEvent("warn", "PENDING_OP_RECOVERED_ON_BOOT", pending);
  }
} catch {}

// -------------------------------------------------------------
//  BUILD INFO
// -------------------------------------------------------------
const RAW_BUILD_TIME = import.meta.env.VITE_BUILD_TIME;
const buildTime = RAW_BUILD_TIME || new Date().toLocaleString("pt-PT");

window.__TREKKIN_BUILD_INFO = {
  label: `🕒 Build gerada em ${buildTime}`,
};

logger.info("BOOT", "Build carregada", { buildTime });

console.log("🚀 Trekkin.app — build ativa");
console.log(`🕒 Build gerada em ${buildTime}`);

// -------------------------------------------------------------
//  🧯 GLOBAL ERROR CAPTURE (Android crash hints)
// -------------------------------------------------------------
window.addEventListener("error", (event) => {
  try {
    const err = event?.error;
    if (isQuotaExceededError(err) || isQuotaExceededError({ message: event?.message })) {
      const recovery = purgeStorageForQuota("");
      logger.warn("STORAGE", "Quota error recovered without fatal overlay", {
        removed: recovery?.removed || 0,
        approxBytesFreed: recovery?.bytes || 0,
      });
      logEvent("warn", "STORAGE_QUOTA_WINDOW_ERROR_RECOVERED", {
        removed: recovery?.removed || 0,
        approxBytesFreed: recovery?.bytes || 0,
      });
      event?.preventDefault?.();
      return;
    }
    const pendingOp = getPendingOp();
    const payload = {
      message: event?.message || err?.message || "unknown",
      filename: event?.filename || null,
      lineno: event?.lineno || null,
      colno: event?.colno || null,
      stack: err?.stack || null,
      pendingOp: pendingOp || null,
      href: typeof location !== "undefined" ? location.href : null,
      ua: typeof navigator !== "undefined" ? navigator.userAgent : null,
      build: window.__TREKKIN_BUILD_INFO?.label || null,
    };
    logger.error("WINDOW", "Unhandled error", payload);
    logEvent("error", "WINDOW_ERROR", payload);
    showFatalOverlay(
      "WINDOW_ERROR",
      `${payload?.message || "unknown"}\n${payload?.filename || ""}:${payload?.lineno || 0}:${payload?.colno || 0}\n\n${payload?.stack || ""}`
    );
  } catch {}
});

window.addEventListener("unhandledrejection", (event) => {
  try {
    const reason = event?.reason;
    const pendingOp = getPendingOp();
    const payload = {
      message: reason?.message || String(reason || "unknown"),
      code: reason?.code || null,
      name: reason?.name || null,
      stack: reason?.stack || null,
      pendingOp: pendingOp || null,
      href: typeof location !== "undefined" ? location.href : null,
      ua: typeof navigator !== "undefined" ? navigator.userAgent : null,
      build: window.__TREKKIN_BUILD_INFO?.label || null,
    };
    logger.error("PROMISE", "Unhandled rejection", payload);
    logEvent("error", "UNHANDLED_REJECTION", payload);
    // Uma promise rejeitada não implica que a aplicação deixou de funcionar.
    // O overlay fatal deve ficar reservado a erros de execução/React.
  } catch {}
});

class BootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "", stack: "" };
  }
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: String(error?.message || error || "unknown"),
      stack: String(error?.stack || ""),
    };
  }
  componentDidCatch(error, info) {
    try {
      logger.error("REACT", "BootErrorBoundary", {
        message: String(error?.message || error || "unknown"),
        stack: String(error?.stack || ""),
        componentStack: String(info?.componentStack || ""),
      });
      logEvent("error", "REACT_BOOT_ERROR", {
        message: String(error?.message || error || "unknown"),
        stack: String(error?.stack || ""),
        componentStack: String(info?.componentStack || ""),
      });
      showFatalOverlay(
        "REACT_BOOT_ERROR",
        `${String(error?.message || error || "unknown")}\n\n${String(error?.stack || "")}\n\n${String(info?.componentStack || "")}`
      );
    } catch {}
  }
  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

// -------------------------------------------------------------
//  🔥 DeepLinkHandler — Reset Password Link
// -------------------------------------------------------------
function DeepLinkHandler() {
  const { setActivePanel } = usePanelsStore();

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const mode = url.searchParams.get("mode");
      const oobCode = url.searchParams.get("oobCode");

      if (mode === "resetPassword" && oobCode) {
        logger.info("DEEPLINK", "Reset password detetado");
        setActivePanel("setNewPassword", { oobCode });
      }
    } catch (err) {
      logger.error("DEEPLINK", "Erro ao processar URL", err);
    }
  }, [setActivePanel]);

  return null;
}

// -------------------------------------------------------------
//  RENDER APP
// -------------------------------------------------------------
ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <BootErrorBoundary>
      <DeepLinkHandler />
      <App />
      <ToastContainer />
    </BootErrorBoundary>
  </React.StrictMode>
);

if (shouldEnableRtdbMonitorOnBoot && typeof window !== "undefined") {
  window.setTimeout(() => {
    try {
      startRtdbTrafficMonitor();
    } catch (err) {
      try {
        localStorage.removeItem("trekkin_rtdb_monitor");
      } catch {}
      console.warn("⚠️ RTDB monitor no arranque desativado após falha:", err);
      logger.warn("RTDB_MONITOR", "Falha no arranque; monitor desativado", {
        message: err?.message || String(err || "unknown"),
      });
    }
  }, 0);
}

// -------------------------------------------------------------
//  SERVICE WORKER (PRODUÇÃO)
// -------------------------------------------------------------
const isNativeRuntime =
  typeof window !== "undefined" && !!window?.Capacitor?.isNativePlatform?.();

if (
  "serviceWorker" in navigator &&
  import.meta.env.MODE === "production" &&
  !isNativeRuntime
) {
  navigator.serviceWorker
    .register("/app/sw.js")
    .then((reg) => logger.info("SW", "Service Worker registado", { scope: reg.scope }))
    .catch((err) => logger.error("SW", "Erro ao registar Service Worker", err));
}
